import { getTokenDefinition, getTokens, } from "@bracket/chainography";
import * as E from "@bracket/contractor-next/erc20";
import * as CACHE from "@cachables";
import type { ColateralBalances, LoadedVault, } from "@types";
import { wagmiConfig as config, } from "@wagmiconfig";
import { getAggregateError, } from "@bracket/catch";
import { contract, multiCaller, } from "@bracket/chainography/contracts";


/**
 * Retrieves and calculates manager token balances for a vault
 * 
 * @param vault - The vault to get manager balances for
 * @returns {Promise<ColateralBalances[]>} Array of token balances with their respective percentages
 * 
 * @remarks
 * This function:
 * 1. Gets all platform tokens including BRKTETH
 * 2. Fetches balance and conversion rate for each token
 * 3. Calculates raw balances and counts
 * 4. Sorts tokens with BRKTETH first and by balance amount
 * 5. Calculates percentage distribution across all tokens
 * 6. Returns formatted balance information with percentages
 */
export const getManagerBalances = async (
  vault: LoadedVault,
):Promise<ColateralBalances[]> => {
	if(!vault.version) {
		return await getManagerBalancesV1(vault);
	}
	else {
		return await getManagerBalancesV2(vault);
	}
}


export const getManagerBalancesV1 = async (
	vault: LoadedVault,
):Promise<ColateralBalances[]> => {

	const tokens = getTokens({
		platform_supply: true,	
		withBRKTETH: true,
	});

	const balances: ColateralBalances[] = await Promise.all(tokens.map(async tok => {

		try {

			const [ [ result, ], errors, ] = await multiCaller<[bigint, number]>(config, [
				contract("ERC20", "balanceOf", tok.address, vault.manager.address),
			]);

			if(errors) {
				throw getAggregateError(errors, `Vaults:Resolvers::getManagerBalancesV1 failed to get manager balances for ${tok.symbol}`);
			}

			const rate = await CACHE.convRate({ token: tok, });

			return {
				title: tok.symbol,
				symbol: tok.symbol,
				value: result,
				count: Number(result) / 1e18,
				label: (Number(result) / 1e18).toFixed(2),
				completePercentage: 0,
				rate,
			};

		}
		catch(err) {

			throw getAggregateError(err, `Vaults:Resolvers::getManagerBalancesV1 failed to get manager balances for ${tok.symbol}`);
		}

	}));

	const sorted = balances.sort((tk1, tk2) => {
		if(tk1.symbol === "BRKTETH") return -1;
		if(tk1.count > tk2.count) return -1;
		return 1;
	})

	const total = sorted.reduce((acc, b) => acc + b.count, 0);


	const result = sorted.map(B => {
		B.completePercentage = (B.count / total) * 100;
		if(Number.isNaN(B.completePercentage)) B.completePercentage = 0;
		B.label += ` (${B.completePercentage}%)`;
		return B;
	})


	return result;

}


export const getManagerBalancesV2 = async (
	vault: LoadedVault,
):Promise<ColateralBalances[]> => {

	const denomination = vault.denomination;
	const { decimals, } = vault.denomination;
	const denoToken = await getTokenDefinition(denomination);

	const balances: ColateralBalances[] = await Promise.all([denoToken].map(async tok => {

		try {

			const [ [ result, ], errors, ] = await multiCaller<[bigint, number]>(config, [
				contract("ERC20", "balanceOf", tok.address, vault.manager.address),
			]);

			if(errors) {
				throw getAggregateError(errors, `Vaults:Resolvers::getManagerBalancesV2 failed to get manager balances for ${tok.symbol}`);
			}

			const rate = await CACHE.convRate({ token: tok, });

			return {
				title: denoToken.symbol,
				symbol: denoToken.symbol,
				value: result,
				count: Number(result) / (10**decimals),
				label: (Number(result) / (10**decimals)).toFixed(2),
				completePercentage: 0,
				rate,
			};

		}
		catch(err) {

			throw getAggregateError(err, `Vaults:Resolvers::getManagerBalancesV2 failed to get manager balances for ${tok.symbol}`);
		}

	}));

	return balances;
}
